import { useState } from 'react'
import { CalendarDays, X } from 'lucide-react'
import { format, isValid, parseISO } from 'date-fns'

import { cn } from '@univdiam/ui/lib/utils'
import { Button } from '@univdiam/ui/components/button'
import { Calendar } from '@univdiam/ui/components/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@univdiam/ui/components/popover'
import { dueDateClasses, dueDateHoverClasses, getDueDateInfo } from '@univdiam/ui/lib/dueDate'

type DueDatePickerProps = {
  value?: string | null
  /** Receives `yyyy-MM-dd`, or null when the date is cleared. */
  onChange?: (value: string | null) => void
  disabled?: boolean
  className?: string
}

/**
 * Due date as a colour-coded trigger that opens a shadcn calendar.
 *
 * The selected day is formatted with `format`, never `toISOString`: the latter
 * converts to UTC first, which rolls a date picked in a timezone behind UTC
 * back to the previous day.
 */
function DueDatePicker({ value, onChange, disabled, className }: DueDatePickerProps) {
  const [open, setOpen] = useState(false)
  const { bucket, label, tooltip, inputValue } = getDueDateInfo(value)

  const selected = inputValue ? parseISO(inputValue) : undefined

  // The calendar reports undefined when the already-selected day is clicked
  // again. That must not wipe the due date; clearing is the X button's job.
  const handleSelect = (date: Date | undefined) => {
    if (date && isValid(date)) onChange?.(format(date, 'yyyy-MM-dd'))
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('flex items-center', className)}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            title={tooltip}
            data-due-bucket={bucket}
            className={cn(
              'h-8 gap-1.5 px-2 text-xs font-medium',
              dueDateClasses(bucket),
              dueDateHoverClasses(bucket),
              inputValue && 'rounded-r-none border-r-0'
            )}
          >
            <CalendarDays className="size-3.5" />
            <span className="whitespace-nowrap">{label}</span>
          </Button>
        </PopoverTrigger>

        {inputValue && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            aria-label="Clear due date"
            title="Clear due date"
            onClick={() => onChange?.(null)}
            className={cn('h-8 rounded-l-none px-1.5', dueDateClasses(bucket), dueDateHoverClasses(bucket))}
          >
            <X className="size-3.5" />
          </Button>
        )}
      </div>

      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={handleSelect}
          autoFocus
          captionLayout="dropdown"
        />
      </PopoverContent>
    </Popover>
  )
}

export default DueDatePicker
